import { CommonProps } from '@ncc-frontend/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    IconDefinition,
    faArrowUpToLine,
    faCircleCheck,
    faCircleXmark,
    faClock,
    faQuestionCircle,
    faSpinnerThird,
    faXmark
} from '@fortawesome/pro-solid-svg-icons';

import { TCompleteFile } from 'lib-ui/vv-uploader/vv-types';
import cn from 'core/utils/cn';
import fileSizeFormatter from 'core/formatters/file-size-formatter';

interface NewUploadCardProps extends CommonProps {
    file: TCompleteFile;
    onDeleteFile?: () => void;
    progress?: number;
}

const NewUploadCard = ({
    file,
    onDeleteFile,
    progress
}: NewUploadCardProps) => {
    if (!file) return null;

    const iconMap: Record<string, IconDefinition> = {
        queued: faClock,
        uploading: faArrowUpToLine,
        failed: faCircleXmark,
        incomplete: faQuestionCircle,
        completed: faCircleCheck,
        verifying: faSpinnerThird,
        deleting: faSpinnerThird
    };

    const iconClassMap: Record<string, string> = {
        queued: 'text-general-orange-orange-60',
        uploading: 'text-general-dark-dark-100',
        failed: 'text-general-red-red-100',
        completed: 'text-general-green-100',
        verifying: 'text-general-grey-grey-100 animate-spin',
        deleting: 'text-general-red-red-100 animate-spin',
        incomplete: 'text-general-grey-grey-100'
    };

    const statusMap: Record<string, string> = {
        queued: 'Queued:',
        uploading: 'Uploading:',
        failed: 'Failed:',
        completed: 'Completed:',
        incomplete: 'Incomplete:',
        deleting: 'Deleting:',
        verifying: 'Verifying:'
    };

    const icon = iconMap[file.status];
    const iconClass = iconClassMap[file.status] || 'text-general-dark-dark-100';
    const status = statusMap[file.status];
    const fillWidth = `${progress}%`;

    return (
        <div
            className={cn(
                'w-full relative border-b border-general-grey-grey-30'
            )}
        >
            <div className="flex flex-row justify-between py-4 h-16">
                <div
                    className={cn(
                        'flex flex-row items-center gap-1',
                        file.status === 'queued' &&
                            'text-general-orange-orange-60',
                        file.status === 'failed' && 'text-general-red-red-100',
                        file.status === 'uploading' &&
                            'text-general-dark-dark-100'
                    )}
                >
                    <FontAwesomeIcon icon={icon} className={iconClass} />

                    <span
                        className={cn(
                            'text-xs font-normal',
                            file.status === 'failed' &&
                                'text-general-red-red-100'
                        )}
                    >
                        {status}
                    </span>
                    <span className="text-xs font-medium text-general-dark-dark-100 break-all">
                        {file.fileName}
                    </span>
                    <span className="text-xs font-normal text-general-grey-grey-80">
                        (
                        {file.fileSize && `${fileSizeFormatter(file.fileSize)}`}
                        )
                    </span>
                </div>
                <div className={cn('flex items-center gap-2')}>
                    <div
                        className={cn(
                            'w-[60px] h-[6px] rounded-lg',
                            file.status === 'uploading' &&
                                'bg-brand-escode-neonblue-neonblue-10',
                            file.status === 'queued' &&
                                'bg-general-orange-orange-10',
                            file.status === 'failed' && 'bg-general-red-red-10'
                        )}
                    >
                        <div
                            className={cn(
                                'w-full h-full rounded-lg',
                                file.status === 'uploading' &&
                                    'bg-brand-escode-neonblue-neonblue-120',
                                file.status === 'failed' &&
                                    'bg-general-red-red-100',
                                file.status === 'completed' &&
                                    'bg-general-green-100'
                            )}
                            style={{
                                transition:
                                    'width 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                                width: fillWidth === '0%' ? '6px' : fillWidth,
                                minWidth: '6px' // Ensures the rounded edge is always visible
                            }}
                        />
                    </div>

                    {!['incomplete', 'deleting'].includes(file.status) && (
                        <span className="w-8 text-xs font-semibold mr-2 text-general-dark-dark-100 text-right">
                            {file.status === 'completed'
                                ? '100%'
                                : `${progress || 0}%`}
                        </span>
                    )}

                    <button
                        className="text-gray-900 hover:text-gray-7 focus:outline-none focus:text-gray-500 disabled:hover:cursor-not-allowed"
                        type="button"
                        data-testid={`delete-file-${file.fileName}`}
                        aria-label="Delete file"
                        disabled={file.status === 'deleting'}
                        onClick={onDeleteFile}
                    >
                        <FontAwesomeIcon
                            data-testid="delete-file"
                            icon={faXmark}
                            className="hover:text-general-red-red-100 disabled:hover:text-general-grey-grey-100"
                        />
                    </button>
                </div>
            </div>
        </div>
    );
};

export default NewUploadCard;
